// GET /forecast/report carries an optional field, only when the (server-side)
// `sooth-forecast-alternate-spaces` GOFF flag is on for the forecast server's
// environment:
//   alternate_forecast: null | {space_key, space, label_map, definitions, pmf}
//
// `alternate_forecast` is a split refinement Mirabilis proposed during the run: the
// question's own options with one or more of them (usually "other") split into finer
// named candidates. `space` is a sooth-idl CategoricalSpace. `pmf` maps every outcome
// label to its probability. `label_map` sends EVERY alternate label to the question
// option it refines, and `definitions` carries exactly the NEW labels (those not among
// the question's options), so the definitions' keys are the new candidates the report's
// alternate-space section lists.
//
// Rendering lives in ForecastReportModal.jsx, gated on the CLIENT-side
// `chatboard-alternate-spaces` flag (server/flags/alternateSpaces.js).

import { isConclusiveResolutionStatus } from "../report/resolutionBody.js";
import { VOIDED_CONTRACT_STATUS } from "./contractStatus.js";
import { bucketLabel, extractMultiway, namesAnOutcome } from "./multiway.js";

const OTHER_LABEL = "other";

const _isPlainObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);

/**
 * @param {any} space
 * @returns {string[]|null}
 */
function _outcomeLabels(space) {
  if (!_isPlainObject(space) || space.kind !== "categorical" || !Array.isArray(space.labels))
    return null;
  if (!space.labels.every((l) => typeof l === "string" && l)) return null;
  return space.allow_other === true ? [...space.labels, OTHER_LABEL] : [...space.labels];
}

/**
 * `reportRowKey` is carried on the result so each alternate keeps its OWN report
 * identity; its alternate-space resolution is read by that key, never by whichever
 * fire happens to be live.
 *
 * @param {any} payload a GET /forecast/report response body
 * @param {string|null} [reportRowKey] the row key the payload was read under
 * @returns {{
 *   spaceKey: string|null,
 *   reportRowKey: string|null,
 *   buckets: Array<{label: string, p: number, definition: string|null}>,
 *   newBuckets: Array<{label: string, p: number, definition: string|null}>,
 * }|null}
 */
export function extractAlternateForecast(payload, reportRowKey = null) {
  const alt = _isPlainObject(payload) ? payload.alternate_forecast : null;
  if (!_isPlainObject(alt) || !_isPlainObject(alt.pmf)) return null;
  const labels = _outcomeLabels(alt.space);
  if (!labels) return null;
  const pmf = alt.pmf;
  if (!labels.every((l) => typeof pmf[l] === "number" && Number.isFinite(pmf[l]))) return null;
  const definitions = _isPlainObject(alt.definitions) ? alt.definitions : {};
  // `allow_other` appends the catch-all LAST (see _outcomeLabels); flag it by that slot.
  const otherAt = alt.space.allow_other === true ? labels.length - 1 : -1;
  const buckets = labels.map((label, i) => ({
    label,
    p: pmf[label],
    definition: typeof definitions[label] === "string" ? definitions[label] : null,
    ...(i === otherAt ? { isOther: true } : {}),
  }));
  const newBuckets = buckets.filter((b) => b.definition !== null);
  return {
    spaceKey: typeof alt.space_key === "string" ? alt.space_key : null,
    reportRowKey: typeof reportRowKey === "string" && reportRowKey ? reportRowKey : null,
    buckets,
    newBuckets,
  };
}

// GET /api/resolve/alternate?reportRowKey=<row key> maps the contract's resolution
// into ONE report's alternate space (server/proxy/resolveAlternate.js). Its 200 body:
//   {status: "resolved"|"void", outcome_label, outcome_index, void_reason,
//    method: "exact"|"judge", observed_outcome, primary_label, resolved_at, space_key}

const ALTERNATE_METHODS = ["exact", "judge"];

const _nonEmptyString = (v) => typeof v === "string" && v.trim() !== "";

/**
 * Both answers must carry `space_key`, so the caller can refuse an answer for a
 * different space than the one on screen.
 *
 * @param {any} body a GET /api/resolve/alternate response body
 * @returns {{
 *   status: "resolved"|"void",
 *   outcomeLabel: string|null,
 *   outcomeIndex: number|null,
 *   method: "exact"|"judge"|null,
 *   voidReason: string|null,
 *   spaceKey: string,
 * }|null}
 */
export function extractAlternateResolution(body) {
  if (!_isPlainObject(body) || !_nonEmptyString(body.space_key)) return null;
  const method = ALTERNATE_METHODS.includes(body.method) ? body.method : null;
  if (body.status === "resolved") {
    if (!_nonEmptyString(body.outcome_label) || !method) return null;
    return {
      status: "resolved",
      outcomeLabel: body.outcome_label.trim(),
      outcomeIndex: Number.isInteger(body.outcome_index) ? body.outcome_index : null,
      method,
      voidReason: null,
      spaceKey: body.space_key,
    };
  }
  if (body.status === "void") {
    if (body.void_reason != null && typeof body.void_reason !== "string") return null;
    return {
      status: "void",
      outcomeLabel: null,
      outcomeIndex: null,
      method,
      voidReason: _nonEmptyString(body.void_reason) ? body.void_reason.trim() : null,
      spaceKey: body.space_key,
    };
  }
  return null;
}

/**
 * @param {ReturnType<typeof extractAlternateResolution>} resolution
 * @param {string|null} [spaceKey] the on-screen alternate's space key
 * @param {Array<{label: string, isOther?: boolean}>|null} [buckets] the on-screen alternate's
 *   buckets, in slot order; the resolved slot is named from these when the index lands on one
 * @returns {string|null}
 */
export function alternateResolutionLine(resolution, spaceKey = null, buckets = null) {
  if (!resolution) return null;
  if (spaceKey && resolution.spaceKey !== spaceKey) return null;
  if (resolution.status === "resolved") {
    const judged = resolution.method === "judge" ? " (judged)" : "";
    // Named by SLOT when the index lands on a bucket, so the catch-all reads as a group and an
    // authored "Other" keeps its own name.
    const slot =
      Number.isInteger(resolution.outcomeIndex) && Array.isArray(buckets)
        ? buckets[resolution.outcomeIndex]
        : null;
    const label = slot ? bucketLabel(slot) : resolution.outcomeLabel;
    return `Resolved in the alternate space: ${label}${judged}`;
  }
  return resolution.voidReason
    ? `Void in the alternate space: ${resolution.voidReason}`
    : "Void in the alternate space";
}

/**
 * "Settled" gates whether to query the alternate endpoint: an in-flight or `unresolved`
 * attempt is not worth asking about, since the resolve server would only answer 204.
 *
 * @param {any} resolution
 * @returns {boolean}
 */
export function contractResolutionSettled(resolution) {
  if (!_isPlainObject(resolution)) return false;
  const st = resolution.status;
  if (st === "unresolved" || st === "in_flight") return false;
  return (
    isConclusiveResolutionStatus(st) || st === VOIDED_CONTRACT_STATUS || namesAnOutcome(resolution)
  );
}

/**
 * Cache policy: a parsed 200 (a final answer) is cached, and so is a 204 as null
 * ("not yet"; the server never caches it, but one modal session need not re-ask on
 * every render). A malformed 200, a 404 (flag off), a 5xx, or an aborted read is NOT
 * cached, so the next open retries.
 *
 * @param {string|null} reportRowKey
 * @param {{apiGet: Function, cache: object, signal?: AbortSignal}} deps
 * @returns {Promise<ReturnType<typeof extractAlternateResolution>>}
 */
export async function fetchAlternateResolution(reportRowKey, { apiGet, cache, signal }) {
  if (!reportRowKey) return null;
  const cached = cache.get(reportRowKey);
  if (cached !== undefined) return cached;
  const r = await apiGet(
    `/api/resolve/alternate?reportRowKey=${encodeURIComponent(reportRowKey)}`,
    { signal },
  );
  if (signal && signal.aborted) return null;
  if (r && r.status === 200) {
    const parsed = extractAlternateResolution(r.body);
    if (parsed) cache.set(reportRowKey, parsed);
    return parsed;
  }
  if (r && r.status === 204) cache.set(reportRowKey, null);
  return null;
}

// A per-browser debug setting (Settings → debug) makes the alternate the space the
// forecast is SHOWN in (the main view's distribution marker and the report's
// distribution) in place of the report's separate alternate-space section. Display
// only: a substituted source is never written back to the run store.

export const ALTERNATE_SPACE_DEFAULT_KEY = "sooth-alternateSpaceDefault";

/**
 * The substitution also overwrites any top-level `forecast_value` / `outcome_labels`
 * on `src`, because `extractMultiway` prefers the top-level copy. Only a CATEGORICAL
 * question takes it, since the alternate refines the question's named options. Returns
 * the same `src` reference when there is nothing to substitute.
 *
 * @param {any} src
 * @param {ReturnType<typeof extractAlternateForecast>} alt
 * @returns {any}
 */
export function withAlternateSpace(src, alt) {
  if (!_isPlainObject(src) || !alt || !Array.isArray(alt.buckets) || alt.buckets.length === 0)
    return src;
  if (extractMultiway(src)?.kind !== "categorical") return src;
  const labels = alt.buckets.map((b) => b.label);
  // Keep a trailing catch-all as the space's implicit `allow_other` slot, so the main view
  // still treats it as the catch-all rather than one more named outcome.
  const hasOther = alt.buckets[alt.buckets.length - 1]?.isOther === true;
  const forecastValue = {
    outcome_space: {
      kind: "categorical",
      labels: hasOther ? labels.slice(0, -1) : labels,
      allow_other: hasOther,
    },
    representation: { form: "pmf", mass: alt.buckets.map((b) => b.p) },
  };
  // A bare contract carries the distribution itself; anything else carries a contract.
  if (!_isPlainObject(src.contract) && "forecast_value" in src)
    return { ...src, forecast_value: forecastValue, outcome_labels: labels };
  const out = {
    ...src,
    contract: { ...(src.contract || {}), forecast_value: forecastValue, outcome_labels: labels },
  };
  if ("forecast_value" in src) out.forecast_value = forecastValue;
  if ("outcome_labels" in src) out.outcome_labels = labels;
  return out;
}

/**
 * The contract's own resolution names its outcome in the QUESTION's space, by label
 * or by PMF slot, and either can land on the wrong alternate bucket ("other" survives
 * as the residual; slot numbers differ). So the naming fields are replaced by the
 * alternate-space answer's label, or cleared when there is none yet, which leaves the
 * bars standing rather than guessing a winner.
 *
 * @param {any} resolution
 * @param {ReturnType<typeof extractAlternateResolution>} altResolution
 * @param {string|null} [spaceKey] the on-screen alternate's space key
 * @returns {any}
 */
export function alternateDistributionResolution(resolution, altResolution, spaceKey = null) {
  if (!_isPlainObject(resolution)) return resolution ?? null;
  const applies =
    altResolution &&
    altResolution.status === "resolved" &&
    (!spaceKey || altResolution.spaceKey === spaceKey);
  const rest = { ...resolution };
  delete rest.resolved_outcome;
  // The alternate's own slot rides along, so an ambiguous label (an authored "Other" beside
  // the catch-all) still settles on the slot the endpoint named.
  if (applies && Number.isInteger(altResolution.outcomeIndex))
    rest.resolved_outcome = { outcome_index: altResolution.outcomeIndex };
  return { ...rest, resolved_bucket_label: applies ? altResolution.outcomeLabel : null };
}

/**
 * The status token is dropped when the resolution names an alternate bucket, so the
 * label decides: the question-space status (`resolved_multi`, or the `unresolved` a
 * lane derives past the deadline for a verdict outside yes/no/tie) would otherwise stop
 * `applyResolutionToDistribution` before it reads the label. A selected attempt that
 * itself says `unresolved` keeps its status, since the alternate answer is the
 * contract's latest and must not settle an older attempt.
 *
 * @param {string|null} status
 * @param {ReturnType<typeof alternateDistributionResolution>} altResolution
 * @param {string|null} [attemptStatus] the selected attempt's own status, when it
 *   differs from `status`
 * @returns {string|null}
 */
export function alternateDistributionStatus(status, altResolution, attemptStatus = status) {
  if (attemptStatus === "unresolved") return status;
  return altResolution?.resolved_bucket_label ? null : status;
}
